// TODO: deduplicate

import type { Signature } from '@dfinity/agent';
import { Delegation, DelegationIdentity, SignedDelegation } from '@dfinity/identity';
import { Principal } from '@dfinity/principal';

// The type of response from II as per the spec
interface AuthResponseSuccess {
  kind: 'authorize-client-success';
  delegations: {
    delegation: {
      pubkey: Uint8Array;
      expiration: bigint;
      targets?: Principal[];
    };
    signature: Uint8Array;
  }[];
  userPublicKey: Uint8Array;
}

// Perform a sign in to II using parameters set in this app
export const authWithII = async ({
  url,
  maxTimeToLive,
  derivationOrigin,
  sessionPublicKey,
}: {
  url: string;
  maxTimeToLive?: bigint;
  derivationOrigin?: string;
  sessionPublicKey: Uint8Array;
}): Promise<DelegationIdentity> => {
  // Figure out the II URL to use
  const iiUrl = new URL(url);
  iiUrl.hash = '#authorize';

  // Open an II window and kickstart the flow
  const win = window.open(iiUrl.toString(), 'ii-window');
  if (win === null) {
    throw new Error(`Could not open window for '${iiUrl}'`);
  }

  // Wait for II to say it's ready
  await new Promise<MessageEvent>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', readyHandler);
      win.close();
      reject(new Error('Timeout waiting for II to be ready'));
    }, 15000);

    const readyHandler = (e: MessageEvent) => {
      if (e.data.kind === 'authorize-ready') {
        window.removeEventListener('message', readyHandler);
        clearTimeout(timeoutId);
        resolve(e);
      }
    };

    window.addEventListener('message', readyHandler);
  });

  const request = {
    kind: 'authorize-client',
    sessionPublicKey,
    maxTimeToLive,
    derivationOrigin,
  };

  win.postMessage(request, iiUrl.origin);

  // Wait for the II response and update the local state
  const response = await new Promise<MessageEvent>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      win.close();
      reject(new Error('Timeout waiting for II response'));
    }, 60000);

    const responseHandler = (e: MessageEvent) => {
      if (e.data.kind === 'authorize-client-success') {
        window.removeEventListener('message', responseHandler);
        win.close();

        clearTimeout(timeoutId);
        resolve(e);
      }
    };
    window.addEventListener('message', responseHandler);
  });

  const message = response.data;
  return message;
};

// Infer the type of an array's elements
type ElementOf<Arr> = Arr extends readonly (infer ElementOf)[] ? ElementOf : 'argument is not an array';

export const extractDelegation = (
  signedDelegation: ElementOf<AuthResponseSuccess['delegations']>
): SignedDelegation => ({
  delegation: new Delegation(
    signedDelegation.delegation.pubkey,
    signedDelegation.delegation.expiration,
    signedDelegation.delegation.targets
  ),
  signature: signedDelegation.signature.buffer as Signature /* brand type for agent-js */,
});
